// RT-21: TOTP verification page, shown after successful email+password login
// when the user has 2FA enabled (MFA AAL2 required).

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Spinner } from '../components/ui/Spinner';

export function Verify2FA() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const from       = (location.state as { from?: string })?.from ?? '/dashboard';

  const [code,    setCode]    = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    const digits = code.replace(/\D/g, '');
    if (digits.length !== 6) {
      setError('Digite os 6 dígitos do seu aplicativo autenticador.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get enrolled TOTP factors (supports both old `totp[]` and new `all[]` shapes)
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const allFactors = (factorsData as { all?: { id: string; factor_type: string; status: string }[]; totp?: { id: string }[] } | null);
      const totpFactor = allFactors?.totp?.[0]
        ?? allFactors?.all?.find(f => f.factor_type === 'totp' && f.status === 'verified');
      if (!totpFactor) {
        setError('Nenhum fator de autenticação encontrado.');
        setLoading(false);
        return;
      }

      // Create challenge then verify
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id,
      });
      if (challengeErr || !challenge) {
        setError('Erro ao criar desafio de autenticação.');
        setLoading(false);
        return;
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId:    totpFactor.id,
        challengeId: challenge.id,
        code:        digits,
      });

      if (verifyErr) {
        setError('Código inválido. Verifique seu aplicativo autenticador e tente novamente.');
        setLoading(false);
        return;
      }

      navigate(from, { replace: true });
    } catch {
      setError('Erro ao verificar código. Tente novamente.');
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-base)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
    }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔐</div>
          <h1 className="font-display" style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
            Verificação em dois fatores
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
            Digite o código do seu aplicativo autenticador.
          </p>
        </div>

        <div className="card">
          <form onSubmit={handleVerify} noValidate>

            {error && (
              <div role="alert" style={{
                padding: '0.875rem 1rem',
                backgroundColor: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 'var(--radius-md)',
                color: '#fca5a5',
                fontSize: 'var(--text-label)',
                marginBottom: '1.25rem',
              }}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="totp-code">Código de 6 dígitos</label>
              <input
                ref={inputRef}
                id="totp-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                className="form-input"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                style={{ letterSpacing: '0.35em', fontSize: '1.25rem', textAlign: 'center' }}
                maxLength={6}
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || code.replace(/\D/g, '').length !== 6}
            >
              {loading ? <Spinner size="sm" /> : 'Verificar e entrar'}
            </button>

          </form>
        </div>

        <p style={{ textAlign: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '1.5rem' }}>
          Perdeu acesso ao aplicativo?{' '}
          <a href="mailto:suporte@professya.com" style={{ color: 'var(--petrol)' }}>
            Entre em contato
          </a>
        </p>
      </div>
    </div>
  );
}
